<script setup lang="ts">
definePageMeta({
	layout: 'area-riservata',
	middleware: 'auth',
});

interface AppointmentItem {
	id: number;
	start_datetime: string;
	end_datetime?: string | null;
	customer_end_datetime?: string | null;
	type?: string | null;
	status?: string | null;
	color?: string | null;
	format?: 'in-person' | 'phone' | 'online' | null;
	online_url?: string | null;
	purchased_service?: {
		service?: { name?: string | null } | null;
		used_quantity?: number | null;
		purchased_quantity?: number | null;
	} | null;
	location?: {
		name?: string | null;
		address?: { street?: string | null; city?: { name?: string | null } | null } | null;
	} | number | null;
	staff?: {
		name?: string | null;
		surname?: string | null;
		public_avatar?: string | { id: string } | null;
	} | number | null;
}

const WEEKDAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const { authFetch } = useAuthFetch();
const config = useRuntimeConfig();

const appointments = ref<AppointmentItem[]>([]);
const appointmentsLoading = ref(false);
const selectedDate = ref(new Date());

/** Mese mostrato nel calendario (5 settimane a partire dal lunedì della settimana che contiene il 1°). */
const calendarMonth = ref(new Date());

/** Lunedì della settimana per una data (Lun = 1). */
function getWeekMonday(d: Date): Date {
	const x = new Date(d);
	const day = x.getDay();
	const diff = day === 0 ? -6 : 1 - day;
	x.setDate(x.getDate() + diff);
	x.setHours(0, 0, 0, 0);
	return x;
}

/** Inizio delle 5 settimane: lunedì della settimana che contiene il 1° del mese mostrato. */
const calendarStart = computed(() => {
	const y = calendarMonth.value.getFullYear();
	const m = calendarMonth.value.getMonth();
	return getWeekMonday(new Date(y, m, 1));
});

const calendarEnd = computed(() => {
	const d = new Date(calendarStart.value);
	d.setDate(d.getDate() + 34);
	d.setHours(23, 59, 59, 999);
	return d;
});

function toLocalYMD(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

function firstAppointmentColorForDate(ymd: string): string | null {
	const ofDay = appointments.value
		.filter(a => a.start_datetime.startsWith(ymd))
		.sort((a, b) => a.start_datetime.localeCompare(b.start_datetime));
	const first = ofDay[0];
	if (!first?.color || !String(first.color).trim()) return null;
	const hex = String(first.color).trim();
	return hex.startsWith('#') ? hex : `#${hex}`;
}

type DayAppointmentSummary = { type: string; count: number; color: string; icon: string };
type DayCell = { date: Date; dayNum: number; isSelected: boolean; isToday: boolean; hasAppointments: boolean; firstColor: string | null; appointmentsByType: DayAppointmentSummary[] };

function appointmentTypeIcon(type: string | null | undefined): string {
	if (!type) return 'event';
	const icons: Record<string, string> = {
		workout: 'sports_gymnastics',
		'nutrition-appointment': 'nutrition',
		'health-appointment': 'health_and_safety',
		'mental-health-appointment': 'psychology_alt',
		'fitness-appointment': 'sports_kabaddi',
		vacation: 'beach_access',
		'time-off': 'event_busy',
	};
	return icons[type] ?? 'event';
}

function appointmentTypeColor(type: string | null | undefined): string {
	if (!type) return '#6b7280';
	const colors: Record<string, string> = {
		workout: '#FFA439',
		'nutrition-appointment': '#2ECDA7',
		'health-appointment': '#E35169',
		'mental-health-appointment': '#3399FF',
		'fitness-appointment': '#FFA439',
		vacation: '#A2B5CD',
		'time-off': '#A2B5CD',
	};
	return colors[type] ?? '#6b7280';
}

function appointmentsByTypeForDate(ymd: string): DayAppointmentSummary[] {
	const ofDay = appointments.value
		.filter(a => a.start_datetime.startsWith(ymd))
		.map(a => a.type ?? '');
	const byType = new Map<string, number>();
	for (const t of ofDay) {
		const key = t || 'default';
		byType.set(key, (byType.get(key) ?? 0) + 1);
	}
	return Array.from(byType.entries()).map(([type]) => ({
		type,
		count: byType.get(type)!,
		color: appointmentTypeColor(type === 'default' ? null : type),
		icon: appointmentTypeIcon(type === 'default' ? null : type),
	}));
}

function buildDaysForRange(start: Date, numDays: number): DayCell[] {
	const days: DayCell[] = [];
	const selectedYmd = `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`;
	const today = new Date();
	const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
	const appointmentDates = new Set(appointments.value.map(a => a.start_datetime.slice(0, 10)));
	for (let i = 0; i < numDays; i++) {
		const d = new Date(start);
		d.setDate(start.getDate() + i);
		const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		days.push({
			date: d,
			dayNum: d.getDate(),
			isSelected: ymd === selectedYmd,
			isToday: ymd === todayYmd,
			hasAppointments: appointmentDates.has(ymd),
			firstColor: firstAppointmentColorForDate(ymd),
			appointmentsByType: appointmentsByTypeForDate(ymd),
		});
	}
	return days;
}

/** 5 settimane (5 righe x 7 giorni). */
const calendarWeeks = computed(() => {
	const start = new Date(calendarStart.value);
	const allDays = buildDaysForRange(start, 35);
	const rows: DayCell[][] = [];
	for (let r = 0; r < 5; r++) {
		rows.push(allDays.slice(r * 7, (r + 1) * 7));
	}
	return rows;
});

const calendarTitle = computed(() => {
	const m = calendarMonth.value.getMonth();
	const y = calendarMonth.value.getFullYear();
	return `${MONTHS[m] ?? 'Gennaio'} ${y}`.toUpperCase();
});

const appointmentsForSelectedDay = computed(() => {
	const ymd = toLocalYMD(selectedDate.value);
	return appointments.value.filter(a => a.start_datetime.startsWith(ymd));
});

const selectedDayLabel = computed(() => {
	const d = selectedDate.value;
	const today = new Date();
	if (d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()) {
		return 'In programma oggi';
	}
	return `In programma il ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
});

const selectedDayCardMemoKey = computed(() => {
	const ymd = toLocalYMD(selectedDate.value);
	const ids = appointmentsForSelectedDay.value.map(a => a.id).join(',');
	return [ymd, ids, appointmentsLoading.value] as const;
});

let appointmentsFetchId = 0;
async function loadAppointments(overrideRange?: { from: string; to: string }) {
	const from = overrideRange?.from ?? toLocalYMD(calendarStart.value);
	const to = overrideRange?.to ?? toLocalYMD(calendarEnd.value);
	const thisFetchId = ++appointmentsFetchId;
	appointmentsLoading.value = true;
	try {
		const res = await authFetch<{ appointments: AppointmentItem[] }>('/api/area-riservata/appointments', {
			query: { from, to },
		});
		const list = res?.appointments ?? [];
		if (thisFetchId === appointmentsFetchId) {
			appointments.value = list;
		}
	} catch {
		if (thisFetchId === appointmentsFetchId) {
			appointments.value = [];
		}
	} finally {
		if (thisFetchId === appointmentsFetchId) {
			appointmentsLoading.value = false;
		}
	}
}

function prevMonth() {
	const d = new Date(calendarMonth.value);
	d.setMonth(d.getMonth() - 1);
	calendarMonth.value = d;
	// Mantieni il giorno selezionato nel range visibile, altrimenti primo del mese
	const start = calendarStart.value;
	const end = new Date(start);
	end.setDate(end.getDate() + 34);
	if (selectedDate.value < start || selectedDate.value > end) {
		selectedDate.value = new Date(d.getFullYear(), d.getMonth(), 1);
	}
	loadAppointments();
}

function nextMonth() {
	const d = new Date(calendarMonth.value);
	d.setMonth(d.getMonth() + 1);
	calendarMonth.value = d;
	const start = calendarStart.value;
	const end = new Date(start);
	end.setDate(end.getDate() + 34);
	if (selectedDate.value < start || selectedDate.value > end) {
		selectedDate.value = new Date(d.getFullYear(), d.getMonth(), 1);
	}
	loadAppointments();
}

function selectDay(day: { date: Date }) {
	selectedDate.value = new Date(day.date);
}

function formatTime(iso: string): string {
	const d = new Date(iso);
	return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function appointmentTypeLabel(type: string | null | undefined): string {
	if (!type) return 'Appuntamento';
	const labels: Record<string, string> = {
		workout: 'Allenamento',
		'nutrition-appointment': 'Visita Nutrizione',
		'health-appointment': 'Visita Medica',
		'mental-health-appointment': 'Visita Psicoterapia',
		'fitness-appointment': 'Visita Fitness',
		vacation: 'Ferie',
		'time-off': 'Permesso',
	};
	return labels[type] || type;
}

function appointmentBorderColor(apt: AppointmentItem): string {
	const c = apt.color?.trim();
	if (c) return c.startsWith('#') ? c : `#${c}`;
	return '#0d9488';
}

function staffName(apt: AppointmentItem): string {
	const s = apt.staff;
	if (!s || typeof s === 'number') return '—';
	const name = [s.name, s.surname].filter(Boolean).join(' ');
	return name || '—';
}

function staffAvatarUrl(apt: AppointmentItem): string | null {
	const s = apt.staff;
	if (!s || typeof s === 'number') return null;
	const file = (s as { public_avatar?: string | { id: string } | null }).public_avatar;
	if (!file) return null;
	const id = typeof file === 'string' ? file : file?.id;
	if (!id) return null;
	const base = config.public.directusUrl as string;
	return base ? `${base}/assets/${id}` : null;
}

function appointmentEndTime(apt: AppointmentItem): string | null {
	const iso = apt.customer_end_datetime ?? apt.end_datetime;
	if (!iso) return null;
	return formatTime(iso);
}

function formatLabel(fmt: string | null | undefined): string {
	if (!fmt) return '';
	const labels: Record<string, string> = {
		'in-person': 'In presenza',
		phone: 'Telefono',
		online: 'Online',
	};
	return labels[fmt] ?? fmt;
}

function serviceName(apt: AppointmentItem): string {
	const ps = apt.purchased_service;
	if (!ps || typeof ps === 'number') return '';
	const svc = (ps as { service?: { name?: string | null } | null }).service;
	if (!svc || typeof svc === 'number') return '';
	return (svc as { name?: string | null }).name?.trim() ?? '';
}

function serviceQuantityLabel(apt: AppointmentItem): string {
	const ps = apt.purchased_service;
	if (!ps || typeof ps === 'number') return '';
	const used = (ps as { used_quantity?: number | null }).used_quantity;
	const purchased = (ps as { purchased_quantity?: number | null }).purchased_quantity;
	if (purchased == null && used == null) return '';
	const u = used != null ? Number(used) : 0;
	const p = purchased != null ? Number(purchased) : 0;
	return `(${u}/${p})`;
}

function formatIcon(fmt: string | null | undefined): string {
	if (!fmt) return 'event_available';
	const icons: Record<string, string> = {
		'in-person': 'home',
		phone: 'settings_phone',
		online: 'dataset_linked',
	};
	return icons[fmt] ?? 'event_available';
}

function locationName(apt: AppointmentItem): string {
	const loc = apt.location;
	if (!loc || typeof loc === 'number') return '';
	return (loc as { name?: string | null }).name?.trim() ?? '';
}

const selectedAppointment = ref<AppointmentItem | null>(null);
const cancelLoading = ref(false);
const cancelError = ref<string | null>(null);

function openAppointmentDrawer(apt: AppointmentItem) {
	selectedAppointment.value = apt;
	cancelError.value = null;
}

function closeAppointmentDrawer() {
	selectedAppointment.value = null;
	cancelError.value = null;
}

async function cancelAppointment(apt: AppointmentItem) {
	cancelLoading.value = true;
	cancelError.value = null;
	try {
		await authFetch(`/api/area-riservata/appointments/${apt.id}`, {
			method: 'PATCH',
			body: { status: 'canceled' },
		});
		appointments.value = appointments.value.filter(a => a.id !== apt.id);
		closeAppointmentDrawer();
	} catch (e: any) {
		cancelError.value = e?.data?.statusMessage || e?.message || 'Errore durante l\'annullamento';
	} finally {
		cancelLoading.value = false;
	}
}

function toIcsUtc(iso: string): string {
	const d = new Date(iso);
	return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function downloadAppointmentIcs(apt: AppointmentItem) {
	const summary = serviceName(apt)
		? `${appointmentTypeLabel(apt.type)} : ${serviceName(apt)}`
		: appointmentTypeLabel(apt.type);
	const descParts = [staffName(apt), formatLabel(apt.format), locationName(apt), apt.online_url].filter(Boolean);
	const description = descParts.join(' | ');
	const location = locationName(apt) || '';
	const dtStart = toIcsUtc(apt.start_datetime);
	const endIso = apt.customer_end_datetime ?? apt.end_datetime ?? apt.start_datetime;
	const dtEnd = toIcsUtc(endIso);
	const uid = `wefocus-apt-${apt.id}-${apt.start_datetime.slice(0, 10)}`;
	const dtStamp = toIcsUtc(new Date().toISOString());
	const ics = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//WeFocus//Area Riservata//IT',
		'CALSCALE:GREGORIAN',
		'BEGIN:VEVENT',
		`UID:${uid}`,
		`DTSTAMP:${dtStamp}`,
		`DTSTART:${dtStart}`,
		`DTEND:${dtEnd}`,
		`SUMMARY:${summary.replace(/\n/g, ' ')}`,
		...(description ? [`DESCRIPTION:${description.replace(/\n/g, ' ').replace(/,/g, '\\,')}`] : []),
		...(location ? [`LOCATION:${location.replace(/\n/g, ' ')}`] : []),
		...(apt.online_url ? [`URL:${apt.online_url}`] : []),
		'END:VEVENT',
		'END:VCALENDAR',
	].join('\r\n');
	const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `appuntamento-${apt.id}-${apt.start_datetime.slice(0, 10)}.ics`;
	a.click();
	URL.revokeObjectURL(url);
}

onMounted(() => {
	loadAppointments();
});
</script>

<template>
	<div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		<h1 class="text-2xl font-bold text-gray-900 mb-6">Calendario</h1>

		<section class="border border-gray-200 bg-white overflow-hidden">
			<!-- Intestazione calendario -->
			<div class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
				<div class="flex items-center gap-2">
					<span class="material-symbols-outlined text-gray-600">calendar_month</span>
					<span class="text-sm font-medium text-gray-900">{{ calendarTitle }}</span>
				</div>
				<div class="flex items-center gap-2">
					<button
						type="button"
						class="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
						aria-label="Mese precedente"
						@click="prevMonth"
					>
						<span class="material-symbols-outlined">chevron_left</span>
					</button>
					<button
						type="button"
						class="p-2 text-gray-600 hover:bg-gray-100 transition-colors"
						aria-label="Mese successivo"
						@click="nextMonth"
					>
						<span class="material-symbols-outlined">chevron_right</span>
					</button>
				</div>
			</div>

			<!-- Griglia 5 settimane x 7 giorni -->
			<div class="p-4">
				<div class="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
					<span v-for="label in WEEKDAY_LABELS" :key="label">{{ label }}</span>
				</div>
				<div class="grid gap-1">
					<div
						v-for="(week, wi) in calendarWeeks"
						:key="wi"
						class="grid grid-cols-7 gap-1"
					>
						<button
							v-for="day in week"
							:key="day.date.toISOString()"
							type="button"
							class="relative w-full aspect-square text-sm font-medium transition-colors flex items-center justify-center border-2 text-gray-900 min-w-0"
							:class="{
								'border-black bg-white': day.isSelected && !day.firstColor,
								'bg-white': day.isSelected && day.firstColor,
								'border-transparent': !day.isSelected && !day.isToday && (day.hasAppointments ? day.firstColor : true),
								'bg-[#0d9488]/20': !day.isSelected && day.hasAppointments && !day.firstColor,
								'bg-gray-100 hover:bg-gray-200': !day.isSelected && !day.hasAppointments,
								'border-double': day.isToday,
								'!border-[5px]': day.isToday,
								'border-black': day.isToday && !day.isSelected,
							}"
							:style="day.firstColor && (day.isSelected || day.hasAppointments) ? { borderColor: (day.isToday && !day.isSelected) ? 'black' : (day.isSelected ? day.firstColor : 'transparent'), backgroundColor: day.isSelected ? `${day.firstColor}18` : `${day.firstColor}20` } : {}"
							@click="selectDay(day)"
						>
							<span>{{ day.dayNum }}</span>
							<div
								v-if="day.appointmentsByType.length"
								class="absolute bottom-0.5 right-0.5 flex flex-wrap justify-end gap-0.5 max-w-full"
							>
								<div
									v-for="item in day.appointmentsByType"
									:key="item.type"
									class="flex items-center justify-center w-[6px] h-[6px] sm:min-w-[22px] sm:min-h-[22px] sm:w-auto sm:h-auto sm:px-1 sm:py-1 p-0 text-white shrink-0"
									:style="{ backgroundColor: item.color }"
									:title="appointmentTypeLabel(item.type === 'default' ? null : item.type)"
								>
									<span class="inline max-sm:!hidden material-symbols-outlined text-[20px] leading-none select-none" style="font-size: 18px" aria-hidden="true">{{ item.icon }}</span>
									<span v-if="item.count > 1" class="inline max-sm:!hidden ml-0.5 text-[12px] font-semibold leading-none tabular-nums">{{ item.count }}</span>
								</div>
							</div>
						</button>
					</div>
				</div>
			</div>

			<!-- Lista appuntamenti del giorno selezionato -->
			<div v-memo="selectedDayCardMemoKey" class="border-t border-gray-200 px-4 py-4">
				<h2 class="text-sm font-semibold text-gray-900 mb-3">{{ selectedDayLabel }}</h2>
				<div v-if="appointmentsLoading" class="flex items-center gap-2 py-4 text-gray-500 text-sm">
					<span class="material-symbols-outlined animate-spin">progress_activity</span>
					Caricamento...
				</div>
				<div v-else-if="appointmentsForSelectedDay.length === 0" class="py-4 text-gray-500 text-sm">
					Nessun appuntamento in programma.
				</div>
				<ul v-else class="space-y-4">
					<li
						v-for="apt in appointmentsForSelectedDay"
						:key="apt.id"
						role="button"
						tabindex="0"
						class="border-l-4 bg-white shadow-sm overflow-hidden transition-shadow hover:shadow-md cursor-pointer"
						:style="{ borderLeftColor: appointmentBorderColor(apt) }"
						@click="openAppointmentDrawer(apt)"
						@keydown.enter="openAppointmentDrawer(apt)"
						@keydown.space.prevent="openAppointmentDrawer(apt)"
					>
						<div class="p-4 flex flex-col sm:flex-row sm:items-stretch gap-4">
							<div class="flex items-center gap-3 shrink-0 sm:flex-col sm:flex-col-reverse sm:w-16 sm:items-center sm:self-stretch sm:gap-0">
								<span
									class="material-symbols-outlined text-2xl shrink-0 sm:text-3xl sm:mt-2"
									:style="{ color: appointmentTypeColor(apt.type) }"
									aria-hidden="true"
								>
									{{ appointmentTypeIcon(apt.type) }}
								</span>
								<div class="flex items-center justify-center py-2 sm:py-0 sm:flex-1 sm:min-h-0">
									<div class="w-12 h-12 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-200 shrink-0">
										<img
											v-if="staffAvatarUrl(apt)"
											:src="staffAvatarUrl(apt)!"
											:alt="staffName(apt)"
											class="w-full h-full object-cover"
										>
										<span
											v-else
											class="w-full h-full flex items-center justify-center text-gray-400"
											aria-hidden="true"
										>
											<span class="material-symbols-outlined text-2xl">person</span>
										</span>
									</div>
								</div>
							</div>
							<div class="min-w-0 flex-1 space-y-2">
								<p class="text-sm font-semibold text-gray-900">
									<template v-if="serviceName(apt)">
										{{ appointmentTypeLabel(apt.type) }} : {{ serviceName(apt) }}
										<span v-if="serviceQuantityLabel(apt)" class="font-normal text-gray-600">{{ serviceQuantityLabel(apt) }}</span>
									</template>
									<template v-else>
										{{ appointmentTypeLabel(apt.type) }}
									</template>
								</p>
								<p class="text-sm text-gray-600">
									{{ staffName(apt) }}
								</p>
								<p class="text-sm text-gray-700 tabular-nums">
									<span class="font-medium">Dalle {{ formatTime(apt.start_datetime) }}</span>
									<template v-if="appointmentEndTime(apt)">
										<span class="text-gray-500"> – Alle </span>
										<span>{{ appointmentEndTime(apt) }}</span>
									</template>
								</p>
								<div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
									<span v-if="formatLabel(apt.format)" class="inline-flex items-center gap-1">
										<span class="material-symbols-outlined text-base">{{ formatIcon(apt.format) }}</span>
										{{ formatLabel(apt.format) }}
									</span>
									<template v-if="locationName(apt)">
										<span class="inline-flex items-center gap-1">
											<span class="material-symbols-outlined text-base">location_on</span>
											{{ locationName(apt) }}
										</span>
									</template>
								</div>
								<div v-if="apt.online_url" class="flex flex-wrap items-center gap-2">
									<span class="text-xs text-gray-500 truncate max-w-[200px] sm:max-w-xs">{{ apt.online_url }}</span>
								</div>
							</div>
						</div>
					</li>
				</ul>
			</div>
		</section>

		<!-- Drawer dettaglio appuntamento -->
		<Teleport to="body">
			<div
				v-if="selectedAppointment"
				class="fixed inset-0 z-50 flex"
				aria-modal="true"
				role="dialog"
				aria-labelledby="drawer-appointment-title"
			>
				<button
					type="button"
					class="absolute inset-0 bg-black/50 transition-opacity"
					aria-label="Chiudi"
					@click="closeAppointmentDrawer"
				/>
				<div
					class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
					@click.stop
				>
					<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
						<h2 id="drawer-appointment-title" class="text-lg font-semibold text-gray-900">
							Dettaglio appuntamento
						</h2>
						<button
							type="button"
							class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Chiudi"
							@click="closeAppointmentDrawer"
						>
							<span class="material-symbols-outlined">close</span>
						</button>
					</div>
					<div v-if="selectedAppointment" class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-4">
						<div
							class="border-l-4 bg-white shadow-sm overflow-hidden"
							:style="{ borderLeftColor: appointmentBorderColor(selectedAppointment) }"
						>
							<div class="p-4 flex flex-col sm:flex-row sm:items-stretch gap-4">
								<div class="flex items-center gap-3 shrink-0 sm:flex-col sm:flex-col-reverse sm:w-16 sm:items-center sm:self-stretch sm:gap-0">
									<span
										class="material-symbols-outlined text-2xl shrink-0 sm:text-3xl sm:mt-2"
										:style="{ color: appointmentTypeColor(selectedAppointment.type) }"
										aria-hidden="true"
									>
										{{ appointmentTypeIcon(selectedAppointment.type) }}
									</span>
									<div class="flex items-center justify-center py-2 sm:py-0 sm:flex-1 sm:min-h-0">
										<div class="w-12 h-12 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-200 shrink-0">
											<img
												v-if="staffAvatarUrl(selectedAppointment)"
												:src="staffAvatarUrl(selectedAppointment)!"
												:alt="staffName(selectedAppointment)"
												class="w-full h-full object-cover"
											>
											<span
												v-else
												class="w-full h-full flex items-center justify-center text-gray-400"
												aria-hidden="true"
											>
												<span class="material-symbols-outlined text-2xl">person</span>
											</span>
										</div>
									</div>
								</div>
								<div class="min-w-0 flex-1 space-y-2">
									<p class="text-sm font-semibold text-gray-900">
										<template v-if="serviceName(selectedAppointment)">
											{{ appointmentTypeLabel(selectedAppointment.type) }} : {{ serviceName(selectedAppointment) }}
											<span v-if="serviceQuantityLabel(selectedAppointment)" class="font-normal text-gray-600">{{ serviceQuantityLabel(selectedAppointment) }}</span>
										</template>
										<template v-else>
											{{ appointmentTypeLabel(selectedAppointment.type) }}
										</template>
									</p>
									<p class="text-sm text-gray-600">
										{{ staffName(selectedAppointment) }}
									</p>
									<p class="text-sm text-gray-700 tabular-nums">
										<span class="font-medium">Dalle {{ formatTime(selectedAppointment.start_datetime) }}</span>
										<template v-if="appointmentEndTime(selectedAppointment)">
											<span class="text-gray-500"> – Alle </span>
											<span>{{ appointmentEndTime(selectedAppointment) }}</span>
										</template>
									</p>
									<div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
										<span v-if="formatLabel(selectedAppointment.format)" class="inline-flex items-center gap-1">
											<span class="material-symbols-outlined text-base">{{ formatIcon(selectedAppointment.format) }}</span>
											{{ formatLabel(selectedAppointment.format) }}
										</span>
										<template v-if="locationName(selectedAppointment)">
											<span class="inline-flex items-center gap-1">
												<span class="material-symbols-outlined text-base">location_on</span>
												{{ locationName(selectedAppointment) }}
											</span>
										</template>
									</div>
									<div v-if="selectedAppointment.online_url" class="flex flex-wrap items-center gap-2">
										<span class="text-xs text-gray-500 truncate max-w-full">{{ selectedAppointment.online_url }}</span>
									</div>
								</div>
							</div>
						</div>
						<p v-if="cancelError" class="text-sm text-red-600">{{ cancelError }}</p>
						<button
							type="button"
							class="w-full inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors"
							@click="downloadAppointmentIcs(selectedAppointment)"
						>
							<span class="material-symbols-outlined text-lg">event</span>
							AGGIUNGI AL TUO CALENDARIO
						</button>
						<button
							type="button"
							class="w-full inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors"
							:disabled="cancelLoading"
							@click="cancelAppointment(selectedAppointment)"
						>
							<span v-if="cancelLoading" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
							{{ cancelLoading ? 'ANNULLAMENTO IN CORSO...' : 'ANNULLA APPUNTAMENTO' }}
						</button>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>
